import { Box, Button, TextField, Typography } from '@mui/material';
import type { TypographyProps } from '@mui/material';
import { useUser } from '@clerk/clerk-react';
import { useTranslations } from '@/i18n/use-translations';
import { useNotifications } from '@/hooks/use-notifications';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

const passwordResetSchema = z
  .object({
    currentPassword: z.string().min(1, 'Current password is required'),
    newPassword: z.string().min(12, 'Password must be at least 12 characters'),
    confirmPassword: z.string().min(12, 'Password must be at least 12 characters'),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  });

type PasswordResetForm = z.infer<typeof passwordResetSchema>;

type MySettingsTabProps = {
  tabTitle?: TypographyProps;
};

const PasswordResetTab: React.FC<MySettingsTabProps> = ({ tabTitle }) => {
  const t = useTranslations();
  const { user } = useUser();
  const notifications = useNotifications();
  const [isLoading, setIsLoading] = useState(false);

  const form = useForm<PasswordResetForm>({
    resolver: zodResolver(passwordResetSchema),
    mode: 'onChange',
  });

  const handlePasswordReset = async (data: PasswordResetForm) => {
    setIsLoading(true);
    try {
      if (!user) throw new Error('User not found');

      // Update password
      await user.updatePassword({
        newPassword: data.newPassword,
        currentPassword: data.currentPassword,
      });

      notifications.notifySuccess(t('user.notification.passwordResetSuccess'));
      form.reset();
    } catch (error) {
      console.error('Password reset failed:', error);

      let errorMessage = t('user.notification.passwordResetError');

      if (error instanceof Error) {
        // Handle specific Clerk error messages
        if (error.message.includes('current password')) {
          errorMessage = t('user.notification.incorrectCurrentPassword');
        } else if (error.message.includes('password requirements')) {
          errorMessage = t('user.notification.passwordRequirements');
        } else if (error.message.includes('too weak')) {
          errorMessage = t('user.notification.passwordTooWeak');
        }
      }

      notifications.notifyError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{ p: 2, pt: 0 }}>
      <Typography {...tabTitle}>{t('user.passwordReset.title')}</Typography>
      <form onSubmit={form.handleSubmit(handlePasswordReset)}>
        <TextField
          fullWidth
          type="password"
          variant="standard"
          label={`${t('user.passwordReset.currentPassword')} *`}
          {...form.register('currentPassword')}
          error={!!form.formState.errors.currentPassword}
          helperText={form.formState.errors.currentPassword?.message}
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth
          type="password"
          variant="standard"
          label={`${t('user.passwordReset.newPassword')} *`}
          {...form.register('newPassword')}
          error={!!form.formState.errors.newPassword}
          helperText={form.formState.errors.newPassword?.message}
          inputProps={{
            autoComplete: 'new-password',
            minLength: 12,
          }}
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth
          type="password"
          variant="standard"
          label={`${t('user.passwordReset.confirmPassword')} *`}
          {...form.register('confirmPassword')}
          error={!!form.formState.errors.confirmPassword}
          helperText={form.formState.errors.confirmPassword?.message}
          inputProps={{
            autoComplete: 'new-password',
            minLength: 12,
          }}
          sx={{ mb: 3 }}
        />
        <Button
          type="submit"
          variant="contained"
          disabled={isLoading || !form.formState.isValid}
          sx={{ width: '100%' }}
        >
          {isLoading ? t('common.loading') : t('user.passwordReset.button')}
        </Button>
      </form>
    </Box>
  );
};

export default PasswordResetTab;
